import { useEffect, useState } from 'react';
import { ApiError, call } from '../api';

interface Ix { id?: string; name: string; url: string; apiKey?: string; enabled: boolean }
interface Cfg { configured: boolean; url: string; hasApiKey: boolean; davUser: string; hasDavPass: boolean; indexers: Ix[] }

/**
 * Usenet via NzbDAV / InfiniDysk (alleen de beheerder). tumedia zoekt bij je indexers; afspelen zet de
 * release klaar in NzbDAV en speelt hem via zijn WebDAV-schijf. Sleutels en wachtwoorden worden versleuteld
 * bewaard en zijn hierna niet meer te zien.
 */
export function UsenetSettings() {
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [davUser, setDavUser] = useState('');
  const [davPass, setDavPass] = useState('');
  const [ixs, setIxs] = useState<Ix[]>([]);
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const [tests, setTests] = useState<{ part: string; ok: boolean; message: string }[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    call<Cfg>('/api/usenet/config').then((c) => {
      setCfg(c);
      setUrl(c.url);
      setDavUser(c.davUser);
      setIxs(c.indexers);
    }).catch(() => setCfg(null));
  }, []);

  if (!cfg) return null;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    setTests(null);
    try {
      const c = await call<Cfg>('/api/usenet/config', { method: 'PUT', body: JSON.stringify({ url, apiKey: apiKey || undefined, davUser, davPass: davPass || undefined, indexers: ixs }) });
      setCfg(c);
      setIxs(c.indexers);
      setApiKey('');
      setDavPass('');
      setMsg({ text: 'Opgeslagen. Test hieronder of alles werkt.' });
    } catch (err) {
      setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.', error: true });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTests(null);
    const r = await call<{ results: { part: string; ok: boolean; message: string }[] }>('/api/usenet/test', { method: 'POST' }).catch(() => null);
    setTests(r?.results ?? [{ part: 'Test', ok: false, message: 'Testen mislukt' }]);
    setBusy(false);
  };

  const setIx = (i: number, patch: Partial<Ix>) => setIxs((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <div className="settings-block form-grid">
      <div className="account-head">
        <h2>Usenet (NzbDAV / InfiniDysk)</h2>
        <span className={`status-pill ${cfg.configured ? 'ok' : 'warn'}`}>{cfg.configured ? 'Ingesteld' : 'Niet ingesteld'}</span>
      </div>
      <p className="subtle hint">
        Alleen jij ziet dit. tumedia zoekt bij je indexers; bij afspelen gaat de release naar NzbDAV en speelt hij meteen, zonder eerst te downloaden.
        Andere profielen mogen het alleen als je bij Gebruikers &amp; rechten "Usenet" aanzet.
      </p>
      <label>Adres van NzbDAV<input className="text-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://192.168.1.10:3000" inputMode="url" autoComplete="off" /></label>
      <label>API-sleutel (SABnzbd)<input className="text-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg.hasApiKey ? 'Opgeslagen — alleen invullen om te wijzigen' : ''} autoComplete="off" /></label>
      <label>WebDAV-gebruiker<input className="text-input" value={davUser} onChange={(e) => setDavUser(e.target.value)} autoComplete="off" /></label>
      <label>WebDAV-wachtwoord<input className="text-input" type="password" value={davPass} onChange={(e) => setDavPass(e.target.value)} placeholder={cfg.hasDavPass ? 'Opgeslagen — alleen invullen om te wijzigen' : ''} autoComplete="new-password" /></label>

      <h3 className="bk-sub">Indexers</h3>
      <p className="subtle hint">Newznab-indexers (bijv. NZBGeek, DrunkenSlug). Gebruik je Prowlarr, dan kun je per indexer het Newznab-adres uit Prowlarr gebruiken.</p>
      {ixs.map((x, i) => (
        <div key={x.id ?? `new${i}`} className="usenet-ix">
          <label>Naam<input className="text-input" value={x.name} onChange={(e) => setIx(i, { name: e.target.value })} /></label>
          <label>Adres<input className="text-input" value={x.url} onChange={(e) => setIx(i, { url: e.target.value })} placeholder="https://api.nzbgeek.info" inputMode="url" autoComplete="off" /></label>
          <label>API-sleutel<input className="text-input" type="password" value={x.apiKey ?? ''} onChange={(e) => setIx(i, { apiKey: e.target.value })} placeholder={x.id ? 'Opgeslagen — alleen invullen om te wijzigen' : ''} autoComplete="off" /></label>
          <div className="form-actions">
            <label className="toggle"><input type="checkbox" checked={x.enabled} onChange={(e) => setIx(i, { enabled: e.target.checked })} /><span>Aan</span></label>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setIxs((l) => l.filter((_, j) => j !== i))}>Verwijderen</button>
          </div>
        </div>
      ))}
      <button className="btn btn-sm" type="button" onClick={() => setIxs((l) => [...l, { name: '', url: '', apiKey: '', enabled: true }])}>+ Indexer toevoegen</button>

      {msg && <p className={msg.error ? 'error' : 'subtle'} role="status">{msg.text}</p>}
      <div className="form-actions">
        <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? 'Bezig…' : 'Opslaan'}</button>
        <button className="btn" disabled={busy || !cfg.url} onClick={() => void test()}>Testen</button>
      </div>
      {tests && (
        <ul className="usenet-tests">
          {tests.map((t) => <li key={t.part} className={t.ok ? 'ok' : 'bad'}>{t.ok ? '✓' : '✕'} <strong>{t.part}</strong> · {t.message}</li>)}
        </ul>
      )}
    </div>
  );
}
